<!--
  Angular 15 here, so *ngIf / *ngFor throughout — the @if block syntax used in
  the web app does not exist before Angular 17.
-->
<div class="dl-page">

  <div class="dl-page__header">
    <div class="dl-page__heading">
      <h1 class="dl-page__title">Cases</h1>
      <p class="dl-page__subtitle" *ngIf="!initialLoading">
        {{ totalElements }} case(s) match these filters
      </p>
    </div>
  </div>

  <!-- FILTERS -->
  <div class="dl-card">
    <div class="dl-card__header">
      <h2 class="dl-card__title">Filters</h2>
      <div class="cases-filter-status" *ngIf="refreshing">
        <div class="dl-spinner cases-filter-spinner"></div>
        <span>Applying…</span>
      </div>
    </div>

    <div class="dl-card__body">
      <form [formGroup]="filterForm" class="cases-filter-form">
        <div class="dl-filters">
          <div class="dl-field">
            <label class="dl-field__label" for="searchTerm">Search</label>
            <input
              id="searchTerm"
              type="text"
              formControlName="searchTerm"
              placeholder="ID, patient, doctor or technician"
              class="dl-input">
          </div>

          <div class="dl-field">
            <label class="dl-field__label">Tenant</label>
            <app-searchable-selection
              [items]="tenants"
              [displayProperty]="'contact.name'"
              [valueProperty]="'id'"
              [searchProperties]="['contact.name', 'contact.email']"
              [placeholder]="'All tenants'"
              [selectedValue]="selectedTenantId"
              [dropdownId]="'cases-tenant'"
              (valueSelected)="onTenantSelected($event)">
            </app-searchable-selection>
          </div>

          <div class="dl-field">
            <label class="dl-field__label" for="createdAtFrom">Created from</label>
            <input id="createdAtFrom" type="date" formControlName="createdAtFrom" class="dl-input">
          </div>

          <div class="dl-field">
            <label class="dl-field__label" for="createdAtTo">Created to</label>
            <input id="createdAtTo" type="date" formControlName="createdAtTo" class="dl-input">
          </div>

          <div class="dl-field dl-filters__wide">
            <label class="dl-field__label">Status</label>
            <div class="dl-chips">
              <button
                *ngFor="let status of statusOptions"
                type="button"
                class="dl-chip"
                [class.is-selected]="isStatusSelected(status)"
                [attr.aria-pressed]="isStatusSelected(status)"
                (click)="toggleStatus(status)">
                {{ getCaseStatusLabel(status) }}
              </button>
            </div>
            <small class="dl-field__hint">No selection means any status.</small>
          </div>

          <div class="dl-field">
            <label class="dl-field__label" for="archiveMode">Archived</label>
            <select id="archiveMode" formControlName="archiveMode" class="dl-select">
              <option value="">Active only</option>
              <option value="include">Include archived</option>
              <option value="only">Archived only</option>
            </select>
          </div>

          <div class="dl-field">
            <label class="dl-field__label" for="pageSize">Per page</label>
            <select id="pageSize" (change)="onPageSizeChange($event)" class="dl-select">
              <option [value]="10">10</option>
              <option [value]="20">20</option>
              <option [value]="50">50</option>
              <option [value]="100">100</option>
            </select>
          </div>
        </div>
      </form>
    </div>

    <div class="dl-card__footer">
      <button type="button" (click)="clearFilters()" class="dl-btn dl-btn--ghost">
        <mat-icon>clear</mat-icon>
        Clear filters
      </button>
    </div>
  </div>

  <!-- First load only. Later fetches dim the table in place instead of replacing it. -->
  <div *ngIf="initialLoading" class="dl-loading">
    <div class="dl-spinner"></div>
    <p>Loading cases…</p>
  </div>

  <div *ngIf="error" class="dl-banner dl-banner--error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
  </div>

  <!-- RESULTS -->
  <div
    *ngIf="!initialLoading && (!error || cases.length > 0)"
    class="dl-card"
    [class.dl-is-refreshing]="refreshing">

    <app-empty-state
      *ngIf="cases.length === 0"
      icon="folder_open"
      title="No cases found">
      Nothing matches the current filters. Try widening the date range or clearing the status chips.
    </app-empty-state>

    <table *ngIf="cases.length > 0" class="dl-table dl-table--cards" dlCardTable>
      <thead>
        <tr>
          <th>Case ID</th>
          <th>Patient</th>
          <th>Tenant</th>
          <th>Created</th>
          <th>Status</th>
          <th>Doctor</th>
          <th>Tech</th>
          <th><span class="cases-sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        <tr
          *ngFor="let case of cases; trackBy: trackByCaseId"
          class="dl-row--clickable"
          (click)="viewCaseDetails(case)">
          <td class="dl-cell--title">{{ case.id || 'N/A' }}</td>
          <td>{{ getPatientName(case) }}</td>
          <td (click)="$event.stopPropagation()">
            <ng-container *ngIf="getTenantPartners(case).length; else noTenant">
              <a
                *ngFor="let tenant of getTenantPartners(case); trackBy: trackByPartnerId"
                [routerLink]="['/partners', tenant.id]"
                class="tenant-chip">
                {{ tenant.contact?.name || tenant.id }}
              </a>
            </ng-container>
            <ng-template #noTenant><span class="tenant-empty">—</span></ng-template>
          </td>
          <td>{{ case.createdAt ? (case.createdAt | date:'short') : 'N/A' }}</td>
          <td>
            <span class="case-status" [class]="getCaseStatusClass(case.status || '')">
              {{ getCaseStatusLabel(case.status) }}
            </span>
          </td>
          <td (click)="$event.stopPropagation()">
            <a *ngIf="case.doctor?.id" [routerLink]="['/partners', case.doctor?.id]">{{ getDoctorName(case) }}</a>
            <span *ngIf="!case.doctor?.id">{{ getDoctorName(case) }}</span>
          </td>
          <td (click)="$event.stopPropagation()">
            <a *ngIf="case.tech?.id" [routerLink]="['/partners', case.tech?.id]">{{ getTechName(case) }}</a>
            <span *ngIf="!case.tech?.id">{{ getTechName(case) }}</span>
          </td>
          <td class="dl-cell--bare" (click)="$event.stopPropagation()">
            <button
              type="button"
              class="dl-btn dl-btn--danger-ghost dl-btn--icon dl-btn--sm"
              (click)="askDelete(case)"
              matTooltip="Delete case"
              aria-label="Delete case">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <app-pagination
      [currentPage]="currentPage"
      [pageSize]="pageSize"
      [totalElements]="totalElements"
      [totalPages]="totalPages"
      [loading]="refreshing"
      itemLabel="cases"
      (pageChange)="onPageChange($event)">
    </app-pagination>
  </div>
</div>

<app-confirm-dialog
  [isOpen]="pendingDelete !== null"
  title="Delete case"
  [message]="'Case #' + pendingDelete?.id + ' will be removed permanently. This cannot be undone.'"
  confirmLabel="Delete case"
  confirmClass="dl-btn--danger"
  [busy]="deleting"
  (confirm)="confirmDelete()"
  (cancel)="pendingDelete = null">
</app-confirm-dialog>
